import { bungie } from '../lib/bungie';
import { tablesOf } from '../lib/manifest';

/**
 * Triumph, seal, collectible and stat-tracker definitions (port of the app's TriumphDefs): the trees the game shows
 * under Triumphs and Collections, read from Bungie's database and the roots Bungie's core settings name.
 */

export interface TriumphRecord { name: string; desc: string; icon: string; score: number; objectives: number[]; title: string; titleFemale: string; gilding: boolean; lore: number }
export interface TriumphNode { name: string; desc: string; icon: string; nodes: number[]; records: number[]; seal: number; collectibles: number[]; metrics: number[] }
export interface CollectibleDef { name: string; icon: string; source: string; item: number }
export interface MetricDef { name: string; desc: string; icon: string }
export interface GuardianRankDef { number: number; name: string; node: number }

export interface TriumphDefs {
  roots: { active: number; seals: number; legacy: number; legacySeals: number; collections: number; badges: number };
  more: { lore: number; medals: number; metrics: number; catalysts: number; crafting: number };
  seasonHub: { card: number; challenges: number; seal: number };
  nodes: Map<number, TriumphNode>;
  records: Map<number, TriumphRecord>;
  objectives: Map<number, { text: string; goal: number }>;
  collectibles: Map<number, CollectibleDef>;
  metrics: Map<number, MetricDef>;
  ranks: GuardianRankDef[];
}

let loading: Promise<TriumphDefs> | null = null;

export function triumphDefs(): Promise<TriumphDefs> {
  loading ??= build().catch((e) => { loading = null; throw e; });
  return loading;
}

async function build(): Promise<TriumphDefs> {
  const [settings, t] = await Promise.all([
    bungie<any>('/Settings/'),
    tablesOf('DestinyPresentationNodeDefinition', 'DestinyRecordDefinition', 'DestinyObjectiveDefinition', 'DestinyCollectibleDefinition',
      'DestinyMetricDefinition', 'DestinyEventCardDefinition', 'DestinyGuardianRankDefinition'),
  ]);
  const s = settings.destiny2CoreSettings ?? {};
  const h = (k: string): number => s[k] ?? 0;
  const roots = { active: h('activeTriumphsRootNodeHash'), seals: h('activeSealsRootNodeHash'), legacy: h('legacyTriumphsRootNodeHash'), legacySeals: h('legacySealsRootNodeHash'), collections: h('collectionRootNode'), badges: h('badgesRootNode') };
  const more = { lore: h('loreRootNodeHash'), medals: h('medalsRootNodeHash'), metrics: h('metricsRootNode'), catalysts: h('exoticCatalystsRootNodeHash'), crafting: h('craftingRootNodeHash') };
  const card = h('seasonalHubEventCardHash');
  const cardDef = card ? t.DestinyEventCardDefinition[card] : undefined;
  const seasonHub = {
    card: card || 1,   // 1 = looked for, none this season
    challenges: cardDef?.weeklyChallengesPresentationNodeHash || cardDef?.triumphsPresentationNodeHash || 0,
    seal: cardDef?.sealPresentationNodeHash ?? 0,
  };
  const ranks: GuardianRankDef[] = Object.values<any>(t.DestinyGuardianRankDefinition)
    .filter((r) => r.presentationNodeHash && r.rankNumber > 0)
    .map((r) => ({ number: r.rankNumber, name: r.displayProperties?.name ?? '', node: r.presentationNodeHash }))
    .sort((a, b) => a.number - b.number);

  const defs: TriumphDefs = { roots, more, seasonHub, nodes: new Map(), records: new Map(), objectives: new Map(), collectibles: new Map(), metrics: new Map(), ranks };
  const allNodes = t.DestinyPresentationNodeDefinition;
  const queue = [roots.active, roots.seals, roots.legacy, roots.legacySeals, roots.collections, roots.badges, more.lore, more.medals, more.metrics, more.catalysts, more.crafting,
    seasonHub.challenges, seasonHub.seal, ...ranks.map((r) => r.node)].filter(Boolean);
  while (queue.length) {
    const hash = queue.shift()!;
    if (defs.nodes.has(hash)) continue;
    const d = allNodes[hash];
    if (!d) continue;
    const node: TriumphNode = {
      name: d.displayProperties.name,
      desc: d.displayProperties.description,
      icon: d.originalIcon || d.displayProperties.icon,
      nodes: (d.children?.presentationNodes ?? []).map((c: any) => c.presentationNodeHash).filter(Boolean),
      records: (d.children?.records ?? []).map((c: any) => c.recordHash).filter(Boolean),
      seal: d.completionRecordHash ?? 0,
      collectibles: (d.children?.collectibles ?? []).map((c: any) => c.collectibleHash).filter(Boolean),
      metrics: (d.children?.metrics ?? []).map((c: any) => c.metricHash).filter(Boolean),
    };
    defs.nodes.set(hash, node);
    queue.push(...node.nodes);
  }
  const recordHashes = new Set<number>();
  for (const n of defs.nodes.values()) { n.records.forEach((r) => recordHashes.add(r)); if (n.seal) recordHashes.add(n.seal); }
  for (const rh of recordHashes) {
    const d = t.DestinyRecordDefinition[rh];
    if (!d) continue;
    // a triumph in steps keeps its objectives and score per step
    const steps: any[] = d.intervalInfo?.intervalObjectives ?? [];
    const objectives: number[] = steps.length ? steps.map((i) => i.intervalObjectiveHash) : d.objectiveHashes ?? [];
    const score = steps.length ? steps.reduce((sum, i) => sum + (i.intervalScoreValue ?? 0), 0) : d.completionInfo?.ScoreValue ?? 0;
    defs.records.set(rh, {
      name: d.displayProperties.name, desc: d.displayProperties.description, icon: d.displayProperties.icon, score,
      objectives: objectives.filter(Boolean),
      title: d.titleInfo?.titlesByGender?.Male ?? '', titleFemale: d.titleInfo?.titlesByGender?.Female ?? '',
      gilding: !!d.forTitleGilding, lore: d.loreHash ?? 0,
    });
  }
  for (const r of defs.records.values())
    for (const oh of r.objectives) {
      if (defs.objectives.has(oh)) continue;
      const d = t.DestinyObjectiveDefinition[oh];
      if (d) defs.objectives.set(oh, { text: d.progressDescription, goal: d.completionValue });
    }
  for (const n of defs.nodes.values()) {
    for (const ch of n.collectibles) {
      const d = t.DestinyCollectibleDefinition[ch];
      if (d?.displayProperties.name) defs.collectibles.set(ch, { name: d.displayProperties.name, icon: d.displayProperties.icon, source: d.sourceString ?? '', item: d.itemHash ?? 0 });
    }
    for (const mh of n.metrics) {
      const d = t.DestinyMetricDefinition[mh];
      if (d?.displayProperties.name) defs.metrics.set(mh, { name: d.displayProperties.name, desc: d.displayProperties.description, icon: d.displayProperties.icon });
    }
  }
  return defs;
}

/** Every triumph under a category (its own and every sub-category's). */
export function recordsUnder(defs: TriumphDefs, node: number): Set<number> {
  const all = new Set<number>();
  const walk = (n: number) => {
    const d = defs.nodes.get(n);
    if (!d) return;
    d.records.forEach((r) => all.add(r));
    d.nodes.forEach(walk);
  };
  walk(node);
  return all;
}

/** The node a page should open on: a category with a single child category opens straight on that child. */
export function inner(defs: TriumphDefs, root: number): number {
  let n = root;
  for (;;) {
    const d = defs.nodes.get(n);
    if (!d || d.nodes.length !== 1 || d.records.length || d.metrics.length || !defs.nodes.has(d.nodes[0])) return n;
    n = d.nodes[0];
  }
}
